import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { authFetch } from "../api";
import Header from "../components/Header";

/**
 * Investors landing — the list of investors the current user may see (backend already
 * filters by ownership: admins see all, everyone else only their own / assigned).
 * From here you create a new investor or edit an existing one.
 */

const LIMIT = 20;

const fmtDate = (s) => {
  if (!s) return "—";
  const d = new Date(s);
  return isNaN(d) ? "—" : d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
};

const th = { textAlign: "left", padding: "10px 14px", fontSize: "11.5px", fontWeight: 700, letterSpacing: "0.03em", textTransform: "uppercase", color: "#8a929b", borderBottom: "1px solid #e6e9ee" };
const td = { padding: "12px 14px", fontSize: "13.5px", color: "#2b323b", borderBottom: "1px solid #eef1f4" };

export default function InvestorsListPage() {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    const qs = new URLSearchParams({ page: String(page), limit: String(LIMIT) });
    if (search.trim()) qs.set("search", search.trim());
    authFetch(`/investors?${qs.toString()}`)
      .then((r) => {
        if (r && Array.isArray(r.investors)) {
          setRows(r.investors);
          setTotal(r.total || 0);
        } else {
          setError((r && r.detail) || "Could not load investors.");
        }
      })
      .catch(() => setError("Could not load investors."))
      .finally(() => setLoading(false));
  }, [page, search]);

  // Debounce search; reload on page change immediately.
  useEffect(() => {
    const t = setTimeout(load, search ? 300 : 0);
    return () => clearTimeout(t);
  }, [load, search]);

  const lastPage = Math.max(1, Math.ceil(total / LIMIT));

  return (
    <div style={{ minHeight: "100vh", backgroundColor: "#f4f6f8" }}>
      <Header showLogout={true} />
      <div style={{ maxWidth: "1000px", margin: "0 auto", padding: "28px 24px 80px" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "8px" }}>
          <h1 style={{ margin: 0, fontSize: "26px", fontWeight: 700, color: "#161b22" }}>Investors</h1>
          <button
            type="button"
            onClick={() => navigate("/investors/new")}
            style={{ padding: "10px 18px", borderRadius: "8px", fontSize: "14px", fontWeight: 600, border: "none", backgroundColor: "#161b22", color: "#fff", cursor: "pointer" }}
          >
            + New investor
          </button>
        </div>
        <p style={{ margin: "0 0 20px", color: "#6b7480", fontSize: "14px" }}>
          {total} investor{total === 1 ? "" : "s"} you can access. Click a row to edit.
        </p>

        <input
          value={search}
          onChange={(e) => { setPage(1); setSearch(e.target.value); }}
          placeholder="Search by household name…"
          style={{ width: "100%", maxWidth: "340px", padding: "10px 13px", border: "1px solid #d5dae0", borderRadius: "8px", fontSize: "13.5px", boxSizing: "border-box", marginBottom: "16px" }}
        />

        <div style={{ backgroundColor: "#fff", border: "1px solid #e6e9ee", borderRadius: "12px", overflow: "hidden", boxShadow: "0 1px 3px rgba(20,27,34,0.05)" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr>
                <th style={th}>Household</th>
                <th style={th}>Financing</th>
                <th style={th}>Status</th>
                <th style={th}>Updated</th>
                <th style={{ ...th, textAlign: "right" }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td style={{ ...td, color: "#8a929b" }} colSpan={5}>Loading…</td></tr>
              ) : error ? (
                <tr><td style={{ ...td, color: "#a5281b" }} colSpan={5}>{error}</td></tr>
              ) : rows.length === 0 ? (
                <tr><td style={{ ...td, color: "#8a929b" }} colSpan={5}>
                  No investors yet. Click “New investor” to add one.
                </td></tr>
              ) : (
                rows.map((r) => (
                  <tr
                    key={r.id}
                    onClick={() => navigate(`/investors/${r.id}/edit`)}
                    style={{ cursor: "pointer" }}
                    onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "#f7f9fb")}
                    onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "transparent")}
                  >
                    <td style={{ ...td, fontWeight: 600, color: "#161b22" }}>{r.household_name || `Investor #${r.id}`}</td>
                    <td style={td}>{r.financing_required === true ? "Required" : r.financing_required === false ? "Cash" : "—"}</td>
                    <td style={td}>{r.status || "—"}</td>
                    <td style={td}>{fmtDate(r.updated_at)}</td>
                    <td style={{ ...td, textAlign: "right" }}>
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); navigate(`/investors/${r.id}/edit`); }}
                        style={{ border: "1px solid #d5dae1", background: "#fff", color: "#1976d2", fontSize: "12.5px", fontWeight: 600, padding: "6px 12px", borderRadius: "7px", cursor: "pointer" }}
                      >
                        Edit
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {total > LIMIT && (
          <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: "12px", marginTop: "16px" }}>
            <button type="button" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}
              style={{ padding: "7px 14px", borderRadius: "7px", border: "1px solid #d5dae1", background: "#fff", color: page <= 1 ? "#b8c0ca" : "#3d4753", cursor: page <= 1 ? "not-allowed" : "pointer", fontSize: "13px", fontWeight: 600 }}>
              ← Prev
            </button>
            <span style={{ fontSize: "13px", color: "#6b7480" }}>Page {page} of {lastPage}</span>
            <button type="button" disabled={page >= lastPage} onClick={() => setPage((p) => p + 1)}
              style={{ padding: "7px 14px", borderRadius: "7px", border: "1px solid #d5dae1", background: "#fff", color: page >= lastPage ? "#b8c0ca" : "#3d4753", cursor: page >= lastPage ? "not-allowed" : "pointer", fontSize: "13px", fontWeight: 600 }}>
              Next →
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
